import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function pad2(n: number): string { return String(n).padStart(2, '0') }
function formatLocalDate(year: number, month0: number, day: number): string {
  return `${year}-${pad2(month0 + 1)}-${pad2(day)}`
}

interface Props {
  value: string
  onChange: (date: string) => void
  onClose: () => void
}

export function Calendar({ value, onChange, onClose }: Props): JSX.Element {
  const parsed = (() => {
    const parts = value.split('-')
    const yy = parseInt(parts[0], 10)
    const mm = parseInt(parts[1], 10) - 1
    if (Number.isFinite(yy) && Number.isFinite(mm)) return { yy, mm }
    const now = new Date()
    return { yy: now.getFullYear(), mm: now.getMonth() }
  })()

  const [viewYear, setViewYear] = useState(parsed.yy)
  const [viewMonth, setViewMonth] = useState(parsed.mm)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDocClick = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey) }
  }, [onClose])

  const today = new Date()
  const todayStr = formatLocalDate(today.getFullYear(), today.getMonth(), today.getDate())

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate()

  type Cell = { dateStr: string; day: number; muted: boolean }
  const cells: Cell[] = []
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear
    cells.push({ dateStr: formatLocalDate(prevYear, prevMonth, day), day, muted: true })
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ dateStr: formatLocalDate(viewYear, viewMonth, day), day, muted: false })
  }
  let nextDay = 1
  while (cells.length % 7 !== 0 || cells.length < 35) {
    const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1
    const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear
    cells.push({ dateStr: formatLocalDate(nextYear, nextMonth, nextDay), day: nextDay, muted: true })
    nextDay++
  }

  const currentYear = today.getFullYear()
  const yearOptions: number[] = []
  for (let y = currentYear - 10; y <= currentYear + 5; y++) yearOptions.push(y)

  return (
    <div ref={containerRef} className="w-80 rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-xl" role="dialog" aria-label="Pick a date">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1) } else setViewMonth(viewMonth - 1) }}
          className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100" aria-label="Previous month" type="button">
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select value={viewMonth} onChange={(e) => setViewMonth(parseInt(e.target.value, 10))}
              className="appearance-none rounded-lg border border-[#E5E7EB] bg-white py-1 pl-3 pr-7 text-[14px] text-gray-700 outline-none focus:border-gray-400" aria-label="Month">
              {MONTH_LABELS.map((label, idx) => (<option key={label} value={idx}>{label}</option>))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
          <div className="relative">
            <select value={viewYear} onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
              className="appearance-none rounded-lg border border-[#E5E7EB] bg-white py-1 pl-3 pr-7 text-[14px] text-gray-700 outline-none focus:border-gray-400" aria-label="Year">
              {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
        </div>
        <button onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1) } else setViewMonth(viewMonth + 1) }}
          className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100" aria-label="Next month" type="button">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="mb-1 grid grid-cols-7">
        {DAY_LABELS.map((d) => (<div key={d} className="text-center text-xs text-gray-400">{d}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, idx) => {
          const isSelected = c.dateStr === value
          const isToday = c.dateStr === todayStr
          return (
            <button key={`${c.dateStr}-${idx}`} type="button"
              onClick={() => { onChange(c.dateStr); onClose() }}
              className={[
                'mx-auto h-9 w-9 rounded-full text-center text-sm transition-colors',
                c.muted ? 'text-gray-300' : 'text-gray-700',
                isSelected ? 'bg-brand-green font-semibold text-black' : '',
                !isSelected && isToday ? 'ring-1 ring-brand-green' : '',
                !isSelected && !c.muted ? 'hover:bg-gray-100' : '',
              ].join(' ')}>
              {c.day}
            </button>
          )
        })}
      </div>
    </div>
  )
}
